import { VitalDurationType } from '@fanduel/casino-datadog-logger/src/types';

import { getGameUidFromLocation } from '@/lostHooks/gameUid/getGameUidFromLocation';
import config from '@/config';

/**
 * Local, in-browser replacement for the old RUM duration vitals. Each vital's
 * START/STOP timestamp is stored once per page run and gameUid in localStorage.
 * The run id prevents stale entries from suppressing a later session when
 * pagehide cleanup did not run.
 */
export const VITALS_STORAGE_PREFIX = 'cgw:vitals';

/** Canonical vital names (kept stable so stored entries survive a rename here). */
export const VITAL_NAMES = {
  FD_LAUNCH: 'FD_LAUNCH_DURATION',
  GAME_CLIENT: 'GAME_CLIENT_DURATION',
  GAME_LOAD: 'GAME_LOAD_DURATION',
  GAME_LAUNCH: 'GAME_LAUNCH_DURATION',
  GAME_FETCH: 'GAME_FETCH_DURATION',
  GEOLOCATION: 'GEOLOCATION_DURATION',
  GLS: 'GLS_DURATION',
} as const;

export type VitalName = (typeof VITAL_NAMES)[keyof typeof VITAL_NAMES];

type VitalCheckpoints = { start?: number; stop?: number };
type VitalsRecord = Record<string, VitalCheckpoints>;

const UNKNOWN_GAME_UID = 'unknown';

/** Monotonic attempt within one RUM view (Try again / full relaunch bumps this). */
let launchAttempt = 1;
let vitalsRunId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const getLaunchAttempt = (): number => launchAttempt;

/**
 * Starts a fresh localStorage bucket for the next launch attempt in the same view.
 * Datadog RUM duration vitals dedupe by name per view; callers suffix DD names when attempt > 1.
 */
export const rotateVitalsLaunchAttempt = (): number => {
  launchAttempt += 1;
  vitalsRunId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return launchAttempt;
};

/** Resets attempt/run id (Vitest only). */
export const __resetVitalsRunForTests = () => {
  launchAttempt = 1;
  vitalsRunId = `test-${Math.random().toString(36).slice(2)}`;
};

const ATTEMPT_KEY_PATTERN = /:a(\d+)-/;

export const parseLaunchAttemptFromStorageKey = (key: string): number => {
  const match = key.match(ATTEMPT_KEY_PATTERN);
  if (match) {
    return Number.parseInt(match[1], 10);
  }
  return 1;
};

/** Datadog vital name for the current attempt (attempt 1 keeps canonical dashboard names). */
export const resolveDatadogVitalName = (canonicalName: string): string =>
  launchAttempt <= 1 ? canonicalName : `${canonicalName}__a${launchAttempt}`;

export const isNonProduction = () =>
  process.env.NODE_CONFIG_ENV !== 'prod' ||
  config.domain.includes('.dev') ||
  config.domain.includes('dark.');

/** Resolves the gameUid the current vitals entry is keyed by (URL-derived, session-stable). */
export const getVitalsGameUid = (): string =>
  getGameUidFromLocation() ?? UNKNOWN_GAME_UID;

export const getVitalsStorageKey = (gameUid: string) =>
  `${VITALS_STORAGE_PREFIX}:${gameUid}:a${launchAttempt}-${vitalsRunId}`;

const listStorageKeysForGame = (gameUid: string): string[] => {
  const storage = getStorage();
  if (!storage) {
    return [];
  }

  const prefix = storagePrefixForGame(gameUid);
  const keys: string[] = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (key?.startsWith(prefix)) {
      keys.push(key);
    }
  }
  return keys.sort(
    (left, right) =>
      parseLaunchAttemptFromStorageKey(left) -
      parseLaunchAttemptFromStorageKey(right),
  );
};

const storagePrefixForGame = (gameUid: string) =>
  `${VITALS_STORAGE_PREFIX}:${gameUid}:`;

const getStorage = (): Storage | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const readRecord = (key: string): VitalsRecord => {
  const storage = getStorage();
  if (!storage) {
    return {};
  }

  try {
    const raw = storage.getItem(key);
    return raw ? (JSON.parse(raw) as VitalsRecord) : {};
  } catch {
    return {};
  }
};

const writeRecord = (key: string, record: VitalsRecord) => {
  const storage = getStorage();
  if (!storage) {
    return;
  }

  try {
    storage.setItem(key, JSON.stringify(record));
  } catch {
    // Ignore quota / serialization failures — vitals are best-effort.
  }
};

/**
 * Stores the START or STOP timestamp for a vital under the given gameUid.
 * Each checkpoint is written at most once — later calls for the same
 * vital + checkpoint are ignored so the first START and first STOP win.
 */
export const recordVitalCheckpoint = (
  vitalName: string,
  checkpoint: VitalDurationType,
  gameUid: string = getVitalsGameUid(),
): boolean => {
  const key = getVitalsStorageKey(gameUid);
  const record = readRecord(key);
  const entry = record[vitalName] ?? {};
  const field = checkpoint === VitalDurationType.STOP ? 'stop' : 'start';

  if (entry[field] != null) {
    return false;
  }

  entry[field] = Date.now();
  record[vitalName] = entry;
  writeRecord(key, record);
  return true;
};

/**
 * Reports whether the given vital already has the requested checkpoint stored.
 * Used to tell a completed launch (GAME_LAUNCH STOP recorded == GameReady
 * received) apart from one still in flight.
 */
export const hasVitalCheckpoint = (
  vitalName: string,
  checkpoint: VitalDurationType,
  gameUid: string = getVitalsGameUid(),
): boolean => {
  const record = readRecord(getVitalsStorageKey(gameUid));
  const field = checkpoint === VitalDurationType.STOP ? 'stop' : 'start';
  return record[vitalName]?.[field] != null;
};

/** Drops stored checkpoints for one vital so a retry can record START/STOP again. */
export const removeVitalFromStorage = (
  vitalName: string,
  gameUid: string = getVitalsGameUid(),
): void => {
  const key = getVitalsStorageKey(gameUid);
  const record = readRecord(key);
  if (!(vitalName in record)) {
    return;
  }

  delete record[vitalName];
  writeRecord(key, record);
};

/**
 * Removes stored vital timestamps: the entry for the given gameUid when
 * provided, otherwise every vitals entry (covers gameUid changes / unknowns).
 */
export const clearVitalsStorage = (gameUid?: string) => {
  const storage = getStorage();
  if (!storage) {
    return;
  }

  if (gameUid) {
    const prefix = storagePrefixForGame(gameUid);
    const keysToRemove: string[] = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key?.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => storage.removeItem(key));
    return;
  }

  const keysToRemove: string[] = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (key?.startsWith(`${VITALS_STORAGE_PREFIX}:`)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => storage.removeItem(key));
};

/** Preferred column order for the durations table; unlisted vitals follow in insertion order. */
const VITAL_TABLE_ORDER: readonly string[] = [
  VITAL_NAMES.GAME_LAUNCH,
  VITAL_NAMES.FD_LAUNCH,
  VITAL_NAMES.GAME_LOAD,
];

/** Wall-clock time with millisecond precision (e.g. `12:53:07.482`). */
const formatTimestamp = (epochMs: number): string => {
  const date = new Date(epochMs);
  const time = date.toTimeString().slice(0, 8);
  const millis = String(date.getMilliseconds()).padStart(3, '0');
  return `${time}.${millis}`;
};

type TableRow = { start: string; stop: string; durationMs: number };

const buildTableRows = (
  entries: [string, VitalCheckpoints][],
): Record<string, TableRow> =>
  entries.reduce<Record<string, TableRow>>((acc, [name, { start, stop }]) => {
    if (start != null && stop != null) {
      acc[name] = {
        start: formatTimestamp(start),
        stop: formatTimestamp(stop),
        durationMs: stop - start,
      };
    }
    return acc;
  }, {});

/**
 * Prints two `console.table`s of STOP-START durations for every vital that has
 * both checkpoints: the launch vitals (GAME_LAUNCH, FD_LAUNCH, GAME_LOAD) first,
 * then all remaining vitals. Start/stop are shown as wall-clock times with
 * milliseconds and the duration as a clear millisecond count. No-op in
 * production and when nothing is complete.
 */
const logCompletedVitalsForRecord = (
  completed: [string, VitalCheckpoints][],
) => {
  const launchEntries = VITAL_TABLE_ORDER.flatMap((name) => {
    const entry = completed.find(([entryName]) => entryName === name);
    return entry ? [entry] : [];
  });
  const restEntries = completed.filter(
    ([name]) => !VITAL_TABLE_ORDER.includes(name),
  );

  const launchRows = buildTableRows(launchEntries);
  const restRows = buildTableRows(restEntries);

  if (Object.keys(launchRows).length > 0) {
    console.log('Launch vitals (STOP − START)');
    console.table(launchRows);
  }
  if (Object.keys(restRows).length > 0) {
    console.log('Other vitals (STOP − START)');
    console.table(restRows);
  }
};

export const logVitalsTable = (gameUid: string = getVitalsGameUid()) => {
  if (!isNonProduction()) {
    return;
  }

  const keys = listStorageKeysForGame(gameUid);
  if (keys.length === 0) {
    return;
  }

  const latestKey = keys[keys.length - 1];
  const completed = Object.entries(readRecord(latestKey)).filter(
    ([, { start, stop }]) => start != null && stop != null,
  );
  if (completed.length === 0) {
    return;
  }

  logCompletedVitalsForRecord(completed);
};
